import { DocumentPlusIcon } from '@heroicons/react/24/outline';
import { type ChangeEvent, useId, useRef } from 'react';
import type { IGetApplicationServiceResDataFile } from '@/api/Rover/ApplicationForm/types';
import { Button } from '@/components/shadcn/button';
import {
  type CriticalRepairDocumentField,
  criticalRepairDocumentFields,
} from '@/features/ApplicationForm/FormConfig/CR2025/CRForm/fileMetadata';

export type DisplayApplicationFile = Partial<IGetApplicationServiceResDataFile> & {
  key: string;
  name?: string;
  url?: string;
};

export const criticalRepairDocumentLabels: Record<CriticalRepairDocumentField, string> = {
  proofOfOwnership: 'Proof of Ownership - Title Deed',
  proofOfOccupancy: 'Proof of Occupancy - Utility Bill',
  zeroIncomeAffidavit: 'Zero Income Affidavit',
  bankStatements: 'Bank Statements',
  proofOfIncome: 'Proof of Income',
};

interface DocumentsDetailsUIProps {
  groupedFiles: Record<CriticalRepairDocumentField, DisplayApplicationFile[]>;
  canUploadFiles: boolean;
  isUploading: boolean;
  uploadingField?: CriticalRepairDocumentField;
  onUpload: (field: CriticalRepairDocumentField, file: File) => void;
}

interface DocumentCategoryProps {
  field: CriticalRepairDocumentField;
  files: DisplayApplicationFile[];
  canUploadFiles: boolean;
  isUploading: boolean;
  isUploadLocked: boolean;
  onUpload: (field: CriticalRepairDocumentField, file: File) => void;
}

function DocumentCategory({ field, files, canUploadFiles, isUploading, isUploadLocked, onUpload }: DocumentCategoryProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const label = criticalRepairDocumentLabels[field];

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) onUpload(field, file);
  };

  return (
    <div>
      <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
        <h3 className='text-sm font-medium text-gray-500'>{label}</h3>
        {canUploadFiles && (
          <>
            <input
              ref={fileInputRef}
              className='hidden'
              type='file'
              aria-label={`Upload ${label}`}
              onChange={handleFileChange}
              disabled={isUploadLocked}
            />
            <Button type='button' variant='outline' size='sm' disabled={isUploadLocked} onClick={() => fileInputRef.current?.click()}>
              <DocumentPlusIcon />
              {isUploading ? 'Uploading...' : 'Add file'}
            </Button>
          </>
        )}
      </div>
      <div className='text-sm text-gray-900'>
        {files.length > 0 ? (
          <ul className='list-disc list-inside'>
            {files.map((file, index) => (
              <li key={`${field}-${file.key}-${index}`}>
                {file.url ? (
                  <a href={file.url} target='_blank' rel='noopener noreferrer' className='text-blue-600 underline' download>
                    {file.name || `File ${index + 1}`}
                  </a>
                ) : (
                  <>
                    {file.name || `File ${index + 1}`}
                    <span className='text-red-500 ml-2'>(No download URL found)</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <span className='text-gray-400'>No files uploaded</span>
        )}
      </div>
    </div>
  );
}

export default function DocumentsDetailsUI({
  groupedFiles,
  canUploadFiles,
  isUploading,
  uploadingField,
  onUpload,
}: DocumentsDetailsUIProps) {
  const documentsTitleId = useId();

  return (
    <section aria-labelledby={documentsTitleId}>
      <div className='bg-white shadow sm:rounded-lg'>
        <div className='py-5'>
          <div className='px-4 py-2 sm:px-6 border-l-4 border-primaryColor-normal'>
            <h2 id={documentsTitleId} className='m-0 text-lg leading-6 font-medium text-primaryColor-normal'>
              Documents
            </h2>
          </div>
        </div>
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
          <div className='space-y-4'>
            {criticalRepairDocumentFields.map((field) => (
              <DocumentCategory
                key={field}
                field={field}
                files={groupedFiles[field]}
                canUploadFiles={canUploadFiles}
                isUploading={isUploading && uploadingField === field}
                isUploadLocked={isUploading}
                onUpload={onUpload}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
